import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type ReactNode,
	forwardRef,
	memo,
	useCallback,
	useMemo,
} from 'react';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { type GroupType, PopupSelect, components, type OptionProps } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { TOOLTIP_DELAY } from '../../../common/constants';
import type { CardType, ExtraOption } from '../../../common/types';
import messages from './messages';
import TypeIcon from './type-icon';

const CHEVRON_DOWN = <ChevronDownIcon label="" />;
const CHEVRON_UP = <ChevronUpIcon label="" />;
const ANALYTICS_SUBJECT = 'iccTypeSelect';

const Option = ({ children, ...props }: OptionProps<SelectOption>) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { cardType, renderAdditionalContent, icon } = props.data as TypeOption;
	return (
		<components.Option {...props}>
			<Flex
				alignItems="center"
				xcss={optionWrapperStyles}
				testId="config-in-context-issue-types.ui.type-select-with-add-issue-type-modal.type-select.option"
			>
				{!!cardType && <TypeIcon cardType={cardType} hasMargin />}
				{!!icon && <Box paddingInlineEnd="space.100">{icon}</Box>}
				<Box as="span" xcss={optionContentStyles}>
					{children}
				</Box>
				{!!cardType && !!renderAdditionalContent && renderAdditionalContent(cardType)}
			</Flex>
		</components.Option>
	);
};

type GroupProps = {
	children?: ReactNode;
};

const Group = ({ children }: GroupProps) => (
	<Box paddingBlockStart="space.050" xcss={extraLinksGroupStyles}>
		{children}
	</Box>
);

const COMPONENTS = { Option, Group } as const;

export type TypeOption = {
	label: string;
	value: number | string;
	cardType: CardType;
	icon?: ReactNode;
	optionType: 'card';
	renderAdditionalContent?: (cardType: CardType) => ReactNode;
};

type SelectOption = TypeOption | ExtraOption;

const makeOption = (
	cardType: CardType,
	renderAdditionalContent?: (cardType: CardType) => ReactNode,
): TypeOption => ({
	label: cardType.name,
	value: cardType.id,
	cardType,
	optionType: 'card',
	renderAdditionalContent,
});

export type SelectRef = {
	node?: PopupSelect<SelectOption>;
};

export type Props = {
	cardTypes: CardType[];
	selectedCardTypeId: number | string;
	extraOptions?: ExtraOption[];
	shouldRenderToParent?: boolean;
	renderAdditionalOptionContent?: (cardType: CardType) => ReactNode;
	onSelectCardType: (selectedCardTypeId: number | string) => void;
	isEligibleInContextIssueTypesExperiment?: boolean;
};

const TypeSelect = forwardRef<SelectRef, Props>(
	(
		{
			cardTypes,
			selectedCardTypeId,
			extraOptions,
			shouldRenderToParent = false,
			onSelectCardType,
			renderAdditionalOptionContent,
			isEligibleInContextIssueTypesExperiment,
		},
		ref,
	) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [, fireExperimentExposure] = UNSAFE_noExposureExp('jsw_config_in_context_issue_types_m2');

		const isDefaultItem = cardTypes[0]?.id === selectedCardTypeId;

		const value: TypeOption | null = useMemo(() => {
			const selectedType = cardTypes.find((type) => type.id === selectedCardTypeId);

			return selectedType ? makeOption(selectedType, renderAdditionalOptionContent) : null;
		}, [cardTypes, selectedCardTypeId, renderAdditionalOptionContent]);

		const options = useMemo(() => {
			const results: (SelectOption | GroupType<SelectOption>)[] = cardTypes.map((type) =>
				makeOption(type, renderAdditionalOptionContent),
			);

			// if there are other options already, group the extra links in a new section
			if (extraOptions) {
				if (results.length) {
					results.push({
						options: extraOptions,
					});
				} else {
					// otherwise add them flat
					results.push(...extraOptions);
				}
			}

			return results;
		}, [cardTypes, extraOptions, renderAdditionalOptionContent]);

		const isOptionDisabled = useCallback(
			(option: SelectOption) => option.optionType === 'card' && option.cardType.isDisabled === true,
			[],
		);

		const trigger = useCallback(
			({ isOpen, ...triggerProps }: { isOpen: boolean }) => {
				const triggerContent = (
					<Tooltip content={value?.label ?? null} delay={TOOLTIP_DELAY} hideTooltipOnClick>
						<Box xcss={containerStyles}>
							<Button
								{...triggerProps}
								appearance="subtle"
								spacing="none"
								isSelected={isOpen}
								aria-label={formatMessage(messages.ariaLabel, {
									value: value?.label ?? formatMessage(messages.invalid),
								})}
								testId="config-in-context-issue-types.ui.type-select-with-add-issue-type-modal.type-select.trigger"
							>
								<Flex alignItems="center" xcss={triggerContentStyles}>
									<TypeIcon cardType={get(value, 'cardType')} />

									{isOpen ? CHEVRON_UP : CHEVRON_DOWN}
								</Flex>
							</Button>
						</Box>
					</Tooltip>
				);

				return <Box xcss={typeContainerStyles}>{triggerContent}</Box>;
			},
			[formatMessage, value],
		);

		const onChange = useCallback(
			(option: SelectOption | null) => {
				if (option) {
					if ('cardType' in option && option.cardType != null) {
						onSelectCardType(option.cardType.id);

						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'selectedType',
								actionSubject: ANALYTICS_SUBJECT,
							}),
							{
								issueTypesCount: cardTypes.length,
								issueTypeId: option.cardType.id,
							},
						);
						return;
					}

					if ('onItemSelect' in option && option.onItemSelect) {
						if (option.name) {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'selectedExtraItem',
									actionSubject: ANALYTICS_SUBJECT,
								}),
								option.name,
								{
									issueTypesCount: cardTypes.length,
								},
							);
						} else {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'selectedExtraItem',
									actionSubject: ANALYTICS_SUBJECT,
								}),
								{
									issueTypesCount: cardTypes.length,
								},
							);
						}

						option.onItemSelect();
					}
				}
			},
			[cardTypes.length, createAnalyticsEvent, onSelectCardType],
		);

		const onMenuOpen = useCallback(() => {
			if (isEligibleInContextIssueTypesExperiment) {
				fireExperimentExposure();
			}

			fireUIAnalytics(
				createAnalyticsEvent({ action: 'opened', actionSubject: ANALYTICS_SUBJECT }),
				{
					isDefaultItem,
				},
			);
		}, [
			createAnalyticsEvent,
			isDefaultItem,
			isEligibleInContextIssueTypesExperiment,
			fireExperimentExposure,
		]);

		const setRefNode = useCallback(
			(popupSelectRef: PopupSelect<SelectOption>) => {
				ref && popupSelectRef && set(ref, 'current.node', popupSelectRef);
			},
			[ref],
		);

		if (cardTypes.length === 1 && isEmpty(extraOptions)) {
			const existingCardType = makeOption(cardTypes[0], renderAdditionalOptionContent);

			return (
				<Tooltip content={existingCardType.label} delay={TOOLTIP_DELAY}>
					<Box xcss={[containerStyles, singValueContainerStyles]}>
						<TypeIcon cardType={get(existingCardType, 'cardType')} />
					</Box>
				</Tooltip>
			);
		}

		// to render popup next to the trigger element to achieve DOM order
		const popupStrategy = shouldRenderToParent ? 'fixed' : 'absolute';

		return (
			<PopupSelect
				ref={setRefNode}
				components={COMPONENTS}
				target={trigger}
				options={options}
				isOptionDisabled={isOptionDisabled}
				value={value}
				onChange={onChange}
				onMenuOpen={onMenuOpen}
				isSearchable={false}
				// DSP-2990 - PopupSelect doesn't respect isSearchable, searchThreshold is required
				searchThreshold={Number.MAX_SAFE_INTEGER}
				minMenuWidth="auto"
				classNamePrefix="inline-card-create-type-select"
				testId="config-in-context-issue-types.ui.type-select-with-add-issue-type-modal.type-select"
				popperProps={{
					strategy: popupStrategy,
				}}
			/>
		);
	},
);

// kept here the negative margins from the original dropdown component but ideally these shouldn't exist
const containerStyles = xcss({
	marginLeft: 'space.negative.050',
	marginRight: 'space.negative.050',
});

const triggerContentStyles = xcss({
	marginLeft: 'space.050',
});

const typeContainerStyles = xcss({
	display: 'flex',
	flexShrink: 0,
});

const optionWrapperStyles = xcss({
	padding: 'space.100',
});

const extraLinksGroupStyles = xcss({
	borderTopWidth: '2px',
	borderTopStyle: 'solid',
	borderTopColor: 'color.border',
	marginTop: 'space.050',
});

const optionContentStyles = xcss({
	flex: 1,
});

const singValueContainerStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	padding: 'space.050',
});

export default memo<
	JSX.LibraryManagedAttributes<typeof TypeSelect, ComponentProps<typeof TypeSelect>>
>(TypeSelect);
